/** Shared wander steering used by animals and NPCs. */

import { isFootBlocked, isPathBlocked, moveAxis } from './movement.js';

/** Give up on a walk after this long — a wanderer wedged on geometry re-rolls. */
export const WALK_TIMEOUT_S = 6;
const PICK_ATTEMPTS = 12;
/** Axis-separated steps can bow past the leash; snap only beyond this. */
export const LEASH_OVERSHOOT_TILES = 0.5;

/**
 * Project `(x, y)` onto the leash circle around spawn. `inset` pulls the rim
 * strictly inside so float rounding cannot read a settled actor as out of bounds.
 */
export function clampToLeash(spawnX, spawnY, x, y, radius, inset = 0) {
  const dx = x - spawnX;
  const dy = y - spawnY;
  const range = Math.hypot(dx, dy);
  if (range <= radius) return { x, y };
  const scale = (radius - inset) / range;
  return { x: spawnX + dx * scale, y: spawnY + dy * scale };
}

/**
 * Sample a leash-clamped walk target whose foot box and straight path are clear.
 * Returns null when every attempt is blocked or too close to count as a step.
 */
export function pickWanderTarget(actor, world, {
  radius, stepMin, stepMax, minDistance, inset = 0,
}) {
  for (let attempt = 0; attempt < PICK_ATTEMPTS; attempt++) {
    const angle = actor.random() * Math.PI * 2;
    const distance = stepMin + actor.random() * (stepMax - stepMin);
    // Clamp instead of rejecting out-of-leash candidates: an actor caught
    // outside a shrunk radius must still find its way home, and rejection
    // would starve it of targets forever.
    const point = clampToLeash(
      actor.spawnX,
      actor.spawnY,
      actor.x + Math.cos(angle) * distance,
      actor.y + Math.sin(angle) * distance,
      radius,
      inset,
    );
    if (Math.hypot(point.x - actor.x, point.y - actor.y) < minDistance) continue;
    if (isFootBlocked(world, point.x, point.y)) continue;
    if (isPathBlocked(world, actor.x, actor.y, point.x, point.y)) continue;
    return point;
  }
  return null;
}

/**
 * Face the target and enter the walk state. Returns false when no target
 * cleared the path check — the caller should re-roll its idle/graze timer.
 */
export function startWanderWalk(actor, world, options) {
  const target = pickWanderTarget(actor, world, options);
  if (!target) return false;
  actor.targetX = target.x;
  actor.targetY = target.y;
  options.faceToward(target.x - actor.x, target.y - actor.y);
  actor.state = 'walk';
  actor.stateTime = 0;
  return true;
}

/**
 * Keep the actor on its leash. A small overshoot is left alone so walking the
 * rim does not snap-and-idle. Far outside with a blocked rim retargets spawn
 * (head home); otherwise snap onto the leash and rest.
 * Returns true when the walk was aborted.
 */
export function constrainToLeash(actor, world, { radius, inset = 0, rest }) {
  const clamped = clampToLeash(actor.spawnX, actor.spawnY, actor.x, actor.y, radius, inset);
  if (Math.hypot(actor.x - clamped.x, actor.y - clamped.y) < LEASH_OVERSHOOT_TILES) return false;
  if (isFootBlocked(world, clamped.x, clamped.y)) {
    actor.targetX = actor.spawnX;
    actor.targetY = actor.spawnY;
    return false;
  }
  actor.x = clamped.x;
  actor.y = clamped.y;
  actor.targetX = actor.x;
  actor.targetY = actor.y;
  rest();
  return true;
}

/**
 * Step toward the current target, sliding on each axis independently. `rest`
 * fires on arrival, a wedge, or walk timeout. `constrain`, if given, runs after
 * each move (animals snap/head-home; NPCs walk a shrunk leash instead).
 */
export function advanceWanderWalk(actor, world, dt, {
  speed, faceToward, rest, constrain = null, onStep = null,
}) {
  actor.stateTime += dt;
  onStep?.(dt);
  const dx = actor.targetX - actor.x;
  const dy = actor.targetY - actor.y;
  const distance = Math.hypot(dx, dy);
  const step = speed * dt;
  if (distance <= step) {
    moveAxis(actor, world, 'x', dx);
    moveAxis(actor, world, 'y', dy);
    constrain?.();
    if (actor.state === 'walk') rest();
    return;
  }
  const movedX = moveAxis(actor, world, 'x', (dx / distance) * step);
  const movedY = moveAxis(actor, world, 'y', (dy / distance) * step);
  faceToward(dx, dy);
  if (constrain?.()) return;
  if ((!movedX && !movedY) || actor.stateTime >= WALK_TIMEOUT_S) rest();
}
